import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import styled from 'styled-components/native'

import { getStartOfNextQuarterByWeeks } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { DashboardUpdateInfo } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dashboardUpdateInfo/DashboardUpdateInfo'
import { DropdownButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import { ExternalLinkIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExternalLinkIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { LoadingSkeleton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/loadingSkeleton/LoadingSkeleton'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { InlineErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineErrorBanner/InlineErrorBanner'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import ClosedEpisodesClientDetails from './ClosedEpisodesClientDetails'
import ClosedEpisodesCountSnapshot from './ClosedEpisodesCountSnapshot'
import ClosedEpisodesPercentageSnapshot from './ClosedEpisodesPercentageSnapshot'
import { actions as mixpanelActions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import {
  getDropdownItemsFromQuarters,
  getPreviousQuartersSorted,
  getYearMonthDayLocaleByDate,
} from '../../common/utils/utils'
import { getAuthUserId } from '../../data/auth/authSelectors'
import { useGetUtilizationQuartersQuery } from '../clients/clientDetails/data/utilizationApi'
import { useGetClosedEpisodesQuery } from '../episodes/data/episodesApi'

const TitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['24px'],
  backgroundColor: theme.colors.backgroundPrimary,
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const Container = styled.View({
  marginLeft: '84px',
  marginRight: '84px',
})

const Heading = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingTop: theme.spacing['32px'],
  paddingBottom: theme.spacing['32px'],
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  zIndex: 1,
}))

const HeadingTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['24px'],
}))

const SnapshotSection = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  rowGap: theme.spacing['24px'],
}))

const DataBoardContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
  backgroundColor: colors.white,
  padding: `${theme.spacing['32px']} ${theme.spacing['32px']}`,
  border: `1px solid ${colors.ui_oatmeal3}`,
  borderRadius: '16px',
}))

const ClosedEpisodesPercentageSnapshotContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '50%',
  paddingRight: theme.spacing['12px'],
}))

const ClosedEpisodesClientDetailsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '50%',
  paddingLeft: theme.spacing['12px'],
}))

const DropdownContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['16px'],
  position: 'relative',
}))

const FlexRowContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
})

const ErrorContainer = styled.View({
  flexBasis: '100%',
  marginTop: '16px',
  width: '100%',
})

export const MeasurementBasedCare: React.FC = () => {
  const userId = useSelector(getAuthUserId)

  const { data: utilizationQuarters, isLoading: loadingUtilization } = useGetUtilizationQuartersQuery({
    date: getYearMonthDayLocaleByDate(new Date()),
  })
  const { formatMessage } = useIntl()
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null)
  const { shouldShowPastQuartersInBCXDashboards } = useFlags()
  const { availableQuarters, previousQuartersSorted, originalStartDate, originalEndDate, currentQuarter } =
    useMemo(() => {
      const sortedQuarters = getPreviousQuartersSorted({ availableQuarters: utilizationQuarters })
      return {
        availableQuarters: utilizationQuarters ?? {},
        previousQuartersSorted: sortedQuarters,
        originalStartDate:
          sortedQuarters.length && utilizationQuarters ? utilizationQuarters[sortedQuarters[0]][0] : '',
        originalEndDate: sortedQuarters.length && utilizationQuarters ? utilizationQuarters[sortedQuarters[0]][1] : '',
        currentQuarter: sortedQuarters[0],
      }
    }, [utilizationQuarters])

  useEffect(() => {
    if (previousQuartersSorted.length) {
      setSelectedQuarter(previousQuartersSorted[0])
    }
  }, [previousQuartersSorted])

  const startDate = selectedQuarter && availableQuarters ? availableQuarters[selectedQuarter][0] : originalStartDate
  const endDate = selectedQuarter && availableQuarters ? availableQuarters[selectedQuarter][1] : originalEndDate

  const {
    data: closedEpisodes,
    isLoading: isLoadingHistoricData,
    isError: showPastQuarterError,
  } = useGetClosedEpisodesQuery(
    {
      startDate,
      endDate,
      providerId: userId,
    },
    { skip: !startDate || !endDate || !userId },
  )

  const dropdownClickHandler = (quarter: string) => {
    setSelectedQuarter(quarter)
  }

  const onLearnMoreClicked = useCallback(() => {
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: mixpanelActions.LEARN_MORE,
    })
    window.open(
      'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/360042313293-Caseload-Measurement-Based-Care-MBC-Compensation-Criteria',
      '_blank',
    )
  }, [])

  if (loadingUtilization || !utilizationQuarters) {
    return <LoadingIndicator size={45} />
  }
  return (
    <>
      <TitleContainer testID={tID('MeasurementBasedCare-title')}>
        <Title
          size={TitleSize.LARGE}
          text={formatMessage({
            defaultMessage: 'Measurement-Based Care',
            description: 'Header for Measurement Based Care Tab in Caseload',
          })}
          color={colors.ui_oatmeal6}
        />
      </TitleContainer>
      <Container>
        <Heading testID={tID('MeasurementBasedCare-heading')}>
          <HeadingTextContainer>
            <DashboardUpdateInfo
              resetDateTime={getStartOfNextQuarterByWeeks(utilizationQuarters)}
              quarterAndYear={selectedQuarter && selectedQuarter !== currentQuarter ? selectedQuarter : ''}
            />
          </HeadingTextContainer>
          <FlexRowContainer>
            {shouldShowPastQuartersInBCXDashboards && previousQuartersSorted?.length && (
              <DropdownContainer>
                <DropdownButton
                  dropdownItems={getDropdownItemsFromQuarters(previousQuartersSorted, dropdownClickHandler)}
                  dropdownWidth={185}
                />
              </DropdownContainer>
            )}
            <TextButton
              testID={tID('MeasurementBasedCare-learnMoreButton')}
              style={{ alignSelf: 'center' }}
              onPress={onLearnMoreClicked}
              text='Learn more'
              rightIcon={<ExternalLinkIcon size={24} strokeColor={colors.teal4} />}
            />
          </FlexRowContainer>
        </Heading>
        {showPastQuarterError && (
          <ErrorContainer>
            <InlineErrorBanner text='Something went wrong. Please try again later, or contact support for help.' />
          </ErrorContainer>
        )}
        {currentQuarter !== selectedQuarter && (
          <InlineInfoBanner
            marginBottom='0px'
            testId='MeasurementBasedCare-infoBanner'
            text={`You are viewing ${selectedQuarter?.replace(
              '_',
              ' ',
            )} data. If manual adjustments were made after the quarter they will not be reflected here.`}
          />
        )}
        <SnapshotSection>
          <ClosedEpisodesPercentageSnapshotContainer>
            <DataBoardContainer>
              {isLoadingHistoricData ? (
                <LoadingSkeleton width='100%' height={252} />
              ) : (
                <ClosedEpisodesPercentageSnapshot closedEpisodes={closedEpisodes} />
              )}
            </DataBoardContainer>
          </ClosedEpisodesPercentageSnapshotContainer>
          <ClosedEpisodesClientDetailsContainer>
            <DataBoardContainer>
              {isLoadingHistoricData ? (
                <LoadingSkeleton width='100%' height={252} />
              ) : (
                <ClosedEpisodesCountSnapshot closedEpisodes={closedEpisodes} />
              )}
            </DataBoardContainer>
          </ClosedEpisodesClientDetailsContainer>
        </SnapshotSection>
        <DataBoardContainer>
          {isLoadingHistoricData ? (
            <LoadingSkeleton width='100%' height={252} />
          ) : (
            <ClosedEpisodesClientDetails closedEpisodes={closedEpisodes} />
          )}
        </DataBoardContainer>
      </Container>
    </>
  )
}

export default MeasurementBasedCare
